/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useContact.ts
 * PURPOSE: Load contact for drawer (API with mock fallback), optimistic `patchContact` with API rollback on failure
 * DEPENDENCIES: react, @shared/lib/frontend/api/contactApi, ../data/mockContactsList, ../data/opportunityDrawerOptions, ../data/scheduleModalCatalog, ../utils/crmFormatters
 * PUBLIC_API: useContact, patchContact (via hook result), ContactDrawerModel, ContactPatch, CRM_NEW_CONTACT_ID, blankContactDrawerModel
 * SIDE_EFFECTS: setTimeout load; setState; HTTP on patch when API-backed fields change
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 321/350
 * RECENT: 2026-06-05 — **`patchToCrmPatch`** sends **`companyId`** for contact account linking.
 * RECENT_PRIOR: 2026-05-11 TASK Q — **`patchContact`** applies **`toTitleCase`** to **`firstName`** / **`lastName`** before optimistic merge and CRM PATCH.
 * RECENT_PRIOR: 2026-05-08 [2.2.873] Initial **`loading`** true when **`contactId`** is a persisted id (avoids first-frame **`!contact`** error branch before **`useEffect`**); Playwright drawer body — root **`CHANGELOG` [2.2.873]`
 * RECENT_PRIOR: 2026-04-30 [2.2.740] **`patchContact`**, **`ContactPatch`**, extended **`ContactDrawerModel`** — CHANGELOG [2.2.740]
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { crmGet, crmPatch } from '@shared/lib/frontend/api/crmApi';
import { toTitleCase } from '../utils/crmFormatters';
import type { ContactRow } from '../data/mockContactsList';
import { MOCK_CRM_CONTACTS_LIST } from '../data/mockContactsList';
import { MOCK_SCHEDULE_OWNERS } from '../data/scheduleModalCatalog';

/** Sentinel id — `ContactDrawer` create mode; not a persisted mock row id. */
export const CRM_NEW_CONTACT_ID = '__new__';

export type ContactLifecycleStatus = 'active' | 'inactive' | 'lead';

export type ContactDrawerModel = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  company: string;
  companyId: string;
  territoryId: string;
  ownerId: string;
  ownerName: string;
  status: ContactLifecycleStatus;
  tags: string[];
  notes: string;
  linkedInUrl: string;
  leadSource: string;
  /** ISO timestamp — last touch with this contact (editable). */
  lastContactedDateISO?: string;
  /** ISO — record creation (readonly in UI). */
  createdAtISO: string;
  /** ISO — last CRM activity on this contact (readonly). */
  lastActivityDateISO?: string;
  /** Preferred channel for outreach (UI + mock; not all API fields). */
  preferredContactMethod: string;
  /** 0–100 CRM score (UI + mock). */
  leadScore: number;
  /** Industry label (UI + mock). */
  industry: string;
};

export type ContactPatch = Partial<ContactDrawerModel>;

function normalizeContactNameFields(patch: ContactPatch): ContactPatch {
  const out: ContactPatch = { ...patch };
  if (out.firstName !== undefined) out.firstName = toTitleCase(String(out.firstName).toLowerCase());
  if (out.lastName !== undefined) out.lastName = toTitleCase(String(out.lastName).toLowerCase());
  return out;
}

function toIsoDate(d: string): string {
  const t = d.trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(t)) return t;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T12:00:00.000Z`;
  return new Date().toISOString();
}

function pickIndustry(seed: string): string {
  const list = ['Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 'Retail', 'Energy'];
  let n = 0;
  for (let i = 0; i < seed.length; i++) n += seed.charCodeAt(i);
  return list[n % list.length]!;
}

function pickLeadScore(seed: string): number {
  let n = 0;
  for (let i = 0; i < seed.length; i++) n += seed.charCodeAt(i);
  return 20 + (n % 81);
}

export function blankContactDrawerModel(): ContactDrawerModel {
  const owner = MOCK_SCHEDULE_OWNERS[0]!;
  const now = new Date().toISOString();
  return {
    id: CRM_NEW_CONTACT_ID,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
    companyId: '',
    territoryId: '',
    ownerId: owner.id,
    ownerName: owner.name,
    status: 'lead',
    tags: [],
    notes: '',
    linkedInUrl: '',
    leadSource: '',
    lastContactedDateISO: undefined,
    createdAtISO: now,
    lastActivityDateISO: undefined,
    preferredContactMethod: 'email',
    leadScore: 50,
    industry: pickIndustry('new'),
  };
}

function rowToModel(row: ContactRow): ContactDrawerModel {
  const owner = MOCK_SCHEDULE_OWNERS[row.id.length % MOCK_SCHEDULE_OWNERS.length] ?? MOCK_SCHEDULE_OWNERS[0]!;
  return {
    id: row.id,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    phone: row.phone ?? '',
    jobTitle: row.title ?? '',
    company: row.company.name,
    companyId: row.company.id,
    territoryId: row.territoryId?.trim() ?? '',
    ownerId: owner.id,
    ownerName: owner.name,
    status: row.status === 'inactive' ? 'inactive' : 'active',
    tags: [],
    notes: '',
    linkedInUrl: '',
    leadSource: '',
    lastContactedDateISO: row.lastActivityDate ? toIsoDate(row.lastActivityDate) : undefined,
    createdAtISO: toIsoDate(row.createdAt),
    lastActivityDateISO: row.lastActivityDate ? toIsoDate(row.lastActivityDate) : undefined,
    preferredContactMethod: row.preferredContactMethod?.trim() || 'email',
    leadScore: pickLeadScore(row.id),
    industry: pickIndustry(row.id + row.company.name),
  };
}

function crmRowToModel(row: Record<string, unknown>): ContactDrawerModel {
  const ownerPick =
    MOCK_SCHEDULE_OWNERS.find((x) => x.id === String(row.ownerId ?? '')) ?? MOCK_SCHEDULE_OWNERS[0]!;
  const st = String(row.status ?? 'active').toLowerCase();
  const status: ContactLifecycleStatus =
    st === 'inactive' ? 'inactive' : st === 'lead' ? 'lead' : 'active';
  return {
    id: String(row.id ?? ''),
    firstName: String(row.firstName ?? ''),
    lastName: String(row.lastName ?? ''),
    email: String(row.email ?? ''),
    phone: String(row.phone ?? ''),
    jobTitle: String(row.jobTitle ?? ''),
    company: String(row.companyName ?? ''),
    companyId: String(row.companyId ?? ''),
    territoryId: String(row.territoryId ?? '').trim(),
    ownerId: String(row.ownerId ?? ownerPick.id),
    ownerName: ownerPick.name,
    status,
    tags: Array.isArray(row.tags) ? (row.tags as string[]).map(String) : [],
    notes: String(row.notes ?? ''),
    linkedInUrl: String(row.linkedinUrl ?? ''),
    leadSource: String(row.source ?? ''),
    lastContactedDateISO: undefined,
    createdAtISO: String(row.createdAtISO ?? ''),
    lastActivityDateISO: undefined,
    preferredContactMethod: String(row.preferredContactMethod ?? 'email'),
    leadScore: typeof row.leadScore === 'number' ? row.leadScore : pickLeadScore(String(row.id ?? '')),
    industry: pickIndustry(String(row.id ?? '')),
  };
}

/** Maps drawer patch keys to shell CRM PATCH JSON. */
function patchToCrmPatch(patch: ContactPatch): Record<string, unknown> {
  const u: Record<string, unknown> = {};
  if (patch.firstName !== undefined) u.firstName = patch.firstName;
  if (patch.lastName !== undefined) u.lastName = patch.lastName;
  if (patch.email !== undefined) u.email = patch.email;
  if (patch.phone !== undefined) u.phone = patch.phone;
  if (patch.jobTitle !== undefined) u.jobTitle = patch.jobTitle;
  if (patch.company !== undefined) u.companyName = patch.company;
  if (patch.companyId !== undefined) u.companyId = patch.companyId || null;
  if (patch.notes !== undefined) u.notes = patch.notes;
  if (patch.tags !== undefined) u.tags = patch.tags;
  if (patch.ownerId !== undefined) u.ownerId = patch.ownerId;
  if (patch.territoryId !== undefined) u.territoryId = patch.territoryId;
  if (patch.status !== undefined) u.status = patch.status;
  if (patch.linkedInUrl !== undefined) u.linkedInUrl = patch.linkedInUrl;
  if (patch.leadSource !== undefined) u.source = patch.leadSource;
  if (patch.preferredContactMethod !== undefined) u.preferredContactMethod = patch.preferredContactMethod;
  if (patch.leadScore !== undefined) u.leadScore = patch.leadScore;
  return u;
}

function applyPatchToModel(prev: ContactDrawerModel, patch: ContactPatch): ContactDrawerModel {
  const compact = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  ) as ContactPatch;
  const next: ContactDrawerModel = { ...prev, ...compact };
  if (patch.ownerId !== undefined) {
    const o = MOCK_SCHEDULE_OWNERS.find((x) => x.id === patch.ownerId);
    if (o) next.ownerName = o.name;
  }
  if (patch.status !== undefined) {
    next.status =
      patch.status === 'inactive' ? 'inactive' : patch.status === 'lead' ? 'lead' : ('active' as ContactLifecycleStatus);
  }
  if (patch.leadScore !== undefined) {
    const n = Number(patch.leadScore);
    next.leadScore = Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : prev.leadScore;
  }
  return next;
}

/** Fields not returned or not stored on API row — keep from optimistic row after PATCH. */
function mergeServerWithLocalShell(server: ContactDrawerModel, optimistic: ContactDrawerModel): ContactDrawerModel {
  return {
    ...server,
    territoryId: optimistic.territoryId,
    leadSource: optimistic.leadSource,
    linkedInUrl: optimistic.linkedInUrl,
    lastContactedDateISO: optimistic.lastContactedDateISO,
    status: optimistic.status,
    ownerId: optimistic.ownerId,
    ownerName: optimistic.ownerName,
    preferredContactMethod: optimistic.preferredContactMethod,
    leadScore: optimistic.leadScore,
    industry: optimistic.industry,
    companyId: optimistic.companyId,
  };
}

export type UseContactResult = {
  contact: ContactDrawerModel | null;
  loading: boolean;
  error: Error | null;
  /** Optimistic merge + CRM PATCH when API-backed keys present; rolls back model on failure. */
  patchContact: (id: string, patch: ContactPatch) => Promise<void>;
};

export function useContact(contactId: string | null): UseContactResult {
  const [contact, setContact] = useState<ContactDrawerModel | null>(null);
  // True on first paint when we will fetch — avoids one frame with loading false and contact null (error UI flash) before the effect runs.
  const [loading, setLoading] = useState(
    () => Boolean(contactId && contactId !== CRM_NEW_CONTACT_ID)
  );
  const [error, setError] = useState<Error | null>(null);
  const loadGenRef = useRef(0);
  const contactRef = useRef<ContactDrawerModel | null>(null);

  useEffect(() => {
    contactRef.current = contact;
  }, [contact]);

  useEffect(() => {
    if (!contactId) {
      setContact(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (contactId === CRM_NEW_CONTACT_ID) {
      setContact(blankContactDrawerModel());
      setLoading(false);
      setError(null);
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    const t = window.setTimeout(() => {
      void (async () => {
        if (loadGenRef.current !== gen) return;
        try {
          const row = await crmGet<Record<string, unknown>>('contacts', contactId);
          if (loadGenRef.current !== gen) return;
          setContact(crmRowToModel(row));
          setError(null);
        } catch {
          const row = MOCK_CRM_CONTACTS_LIST.find((c) => c.id === contactId);
          if (!row) {
            setContact(null);
            setError(new Error('Contact not found.'));
          } else {
            setContact(rowToModel(row));
            setError(null);
          }
        } finally {
          if (loadGenRef.current === gen) setLoading(false);
        }
      })();
    }, 120);
    return () => window.clearTimeout(t);
  }, [contactId]);

  const patchContact = useCallback(async (id: string, rawPatch: ContactPatch) => {
    if (!id || id === CRM_NEW_CONTACT_ID) return;
    const previous = contactRef.current;
    if (!previous || previous.id !== id) return;

    const patch = normalizeContactNameFields(rawPatch);
    const optimistic = applyPatchToModel(previous, patch);
    setContact(optimistic);

    const apiBody = patchToCrmPatch(patch);
    if (Object.keys(apiBody).length === 0) {
      return;
    }

    try {
      const updated = await crmPatch<Record<string, unknown>>('contacts', id, apiBody);
      const merged = mergeServerWithLocalShell(crmRowToModel(updated), optimistic);
      setContact(merged);
    } catch (e) {
      setContact(previous);
      throw e;
    }
  }, []);

  return { contact, loading, error, patchContact };
}
